import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { Toggle } from '@/components/ui/Toggle';
import { SectionCard } from '@/pages/settings/settingsChrome';
import { useClinicUsersList } from '@/hooks/useClinicUsers';
import { useClinicRoles } from '@/hooks/useClinicRoles';
import {
  useFormsStaffAccess,
  useGrantFormsStaff,
  useRevokeFormsStaff,
  type FormsStaffAccessRow,
} from '@/hooks/useFormsStaffAccess';
import {
  defaultFormsStaffRole,
  isAccountInOtherTenantSyncError,
  isActiveFormsGrant,
  membershipHasQuestionnairesEdit,
  type FormsStaffStoredRole,
} from '@/lib/formsStaffAccess';

function roleLabel(
  role: FormsStaffStoredRole,
  t: (key: string) => string,
): string {
  switch (role) {
    case 'owner':
      return t('questionnaires.access.role_owner');
    case 'admin':
      return t('questionnaires.access.role_admin');
    case 'editor':
      return t('questionnaires.access.role_editor');
    default: {
      const _exhaustive: never = role;
      return _exhaustive;
    }
  }
}

export function FormsStaffAccessSection({ clinicId }: { clinicId: string }) {
  const { t } = useTranslation();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();
  const canManage = isPlatformAdmin || isClinicAdmin(clinicId);
  const members = useClinicUsersList(clinicId);
  const roles = useClinicRoles(clinicId);
  const grants = useFormsStaffAccess(clinicId);
  const grant = useGrantFormsStaff(clinicId);
  const revoke = useRevokeFormsStaff(clinicId);
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  const grantByUser = useMemo(() => {
    const map = new Map<string, FormsStaffAccessRow>();
    for (const row of grants.data ?? []) {
      if (isActiveFormsGrant(row.sync_status)) {
        map.set(row.user_id, row);
      }
    }
    return map;
  }, [grants.data]);

  if (!canManage) return null;

  const handleToggle = async (userId: string, next: boolean, membershipRole: 'main_clinic_admin' | 'clinic_admin' | 'employee') => {
    setRowError((prev) => ({ ...prev, [userId]: '' }));
    setBusyUserId(userId);
    try {
      if (next) {
        await grant.mutateAsync({ userId, role: defaultFormsStaffRole(membershipRole) });
      } else {
        await revoke.mutateAsync({ userId });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t('questionnaires.access.save_error');
      setRowError((prev) => ({
        ...prev,
        [userId]: message === 'forms_owner'
          ? t('questionnaires.access.owner_cannot_remove')
          : message === 'account-in-other-tenant'
            ? t('questionnaires.access.account_in_other_tenant')
            : message,
      }));
    } finally {
      setBusyUserId(null);
    }
  };

  const handleRoleChange = async (userId: string, role: 'admin' | 'editor') => {
    setRowError((prev) => ({ ...prev, [userId]: '' }));
    setBusyUserId(userId);
    try {
      await grant.mutateAsync({ userId, role });
    } catch (err) {
      const message = err instanceof Error ? err.message : t('questionnaires.access.save_error');
      setRowError((prev) => ({ ...prev, [userId]: message }));
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <SectionCard
      className="mb-s4"
      title={t('questionnaires.access.title')}
      subtitle={t('questionnaires.access.intro')}
    >
      <p className={cn(TYPE.body, 'text-ds-muted')}>
        {t('questionnaires.access.explanation')}
      </p>

      {members.isLoading || grants.isLoading || roles.isLoading ? (
        <div className="mt-s4 flex justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-ds-muted" />
        </div>
      ) : null}

      {members.isError || grants.isError ? (
        <p className={cn(TYPE.small, 'mt-s4 text-ds-red-ink')}>
          {t('questionnaires.access.load_error')}
        </p>
      ) : null}

      <ul className="mt-s4 divide-y divide-ds-line rounded-card border border-ds-line">
        {(members.data ?? []).map((member) => {
          const grantRow = grantByUser.get(member.user_id);
          const eligible = membershipHasQuestionnairesEdit(member, roles.data ?? []);
          const granted = Boolean(grantRow);
          const isOwner = grantRow?.role === 'owner';
          const busy = busyUserId === member.user_id;
          const name = member.user?.name?.trim() || member.user?.email || '—';

          return (
            <li
              key={member.id}
              className="flex flex-col gap-s3 p-s4 nav:flex-row nav:items-center nav:justify-between"
            >
              <div className="min-w-0">
                <p className={cn(TYPE.body, 'font-medium text-ds-text')}>{name}</p>
                <p className={cn(TYPE.small, 'text-ds-muted')}>{member.user?.email}</p>
                {!eligible ? (
                  <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>
                    {t('questionnaires.access.needs_questionnaires_edit')}
                  </p>
                ) : null}
                {grantRow?.sync_status === 'error' && grantRow.sync_error ? (
                  <p className={cn(TYPE.small, 'mt-s1 text-ds-red-ink')}>
                    {isAccountInOtherTenantSyncError(grantRow.sync_error)
                      ? t('questionnaires.access.account_in_other_tenant')
                      : grantRow.sync_error}
                  </p>
                ) : null}
                {rowError[member.user_id] ? (
                  <p className={cn(TYPE.small, 'mt-s1 text-ds-red-ink')}>
                    {rowError[member.user_id] === 'account-in-other-tenant'
                      ? t('questionnaires.access.account_in_other_tenant')
                      : rowError[member.user_id] === 'forms_owner'
                        ? t('questionnaires.access.owner_cannot_remove')
                        : rowError[member.user_id]}
                  </p>
                ) : null}
              </div>

              <div className="flex shrink-0 items-center gap-s3">
                {granted && (
                  isOwner ? (
                    <span className={cn(TYPE.small, 'text-ds-muted')}>
                      {roleLabel('owner', t)}
                    </span>
                  ) : (
                    <select
                      aria-label={t('questionnaires.access.role_label')}
                      value={grantRow?.role === 'admin' ? 'admin' : 'editor'}
                      disabled={busy}
                      onChange={(event) => {
                        const next = event.target.value === 'admin' ? 'admin' : 'editor';
                        void handleRoleChange(member.user_id, next);
                      }}
                      className="rounded-lg border border-ds-field bg-ds-card px-3 py-1.5 text-sm text-ds-text shadow-card focus:border-ds-teal focus:outline-none focus:shadow-ring"
                    >
                      <option value="admin">{t('questionnaires.access.role_admin')}</option>
                      <option value="editor">{t('questionnaires.access.role_editor')}</option>
                    </select>
                  )
                )}
                <Toggle
                  checked={granted}
                  disabled={busy || !eligible || isOwner}
                  onCheckedChange={(next) => {
                    void handleToggle(member.user_id, next, member.role);
                  }}
                  label={
                    busy
                      ? t('questionnaires.access.saving')
                      : granted
                        ? t('questionnaires.access.granted')
                        : t('questionnaires.access.not_granted')
                  }
                />
              </div>
            </li>
          );
        })}
      </ul>
    </SectionCard>
  );
}
